import { useEffect, useState } from 'react';
import { api, type UsageReport } from '../api';
import { Page, Card, Select, EmptyState } from '../ui';

const fmtUsd = (n: number) => `US$ ${n.toFixed(n < 1 ? 4 : 2)}`;
const fmtNum = (n: number) => n.toLocaleString('pt-BR');

export function Usage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api.getUsage(days).then(setData).catch(() => setErr('Falha ao carregar uso')).finally(() => setLoading(false));
  }, [days]);

  const maxDay = data ? Math.max(1, ...data.byDay.map((d) => d.costUsd)) : 1;

  return (
    <Page
      title="Uso & Custo"
      subtitle="Tokens (reais) por modelo, tipo e dia — para acompanhar volume e limites do plano. Com token de assinatura do Claude Code você paga o plano fixo, não por request: o custo em US$ é uma estimativa equivalente-API. Captura as execuções da API (chat, onboarding, roteador)."
      actions={
        <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 160 }}>
          <option value="1">último 1 dia</option>
          <option value="7">últimos 7 dias</option>
          <option value="30">últimos 30 dias</option>
          <option value="90">últimos 90 dias</option>
        </Select>
      }
    >
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : !data || data.totals.runs === 0 ? (
        <EmptyState title="Sem uso no período" description="Ainda não há execuções registradas nessa janela." />
      ) : (
        <>
          <div className="usage-cards">
            <Card className="usage-stat"><div className="usage-stat-v">{fmtNum(data.totals.inputTokens + data.totals.outputTokens)}</div><div className="usage-stat-l">tokens (total)</div></Card>
            <Card className="usage-stat"><div className="usage-stat-v">{fmtNum(data.totals.inputTokens)}</div><div className="usage-stat-l">tokens de entrada</div></Card>
            <Card className="usage-stat"><div className="usage-stat-v">{fmtNum(data.totals.outputTokens)}</div><div className="usage-stat-l">tokens de saída</div></Card>
            <Card className="usage-stat"><div className="usage-stat-v">{fmtNum(data.totals.runs)}</div><div className="usage-stat-l">execuções</div></Card>
            <Card className="usage-stat usage-stat-muted"><div className="usage-stat-v">{fmtUsd(data.totals.costUsd)}</div><div className="usage-stat-l">custo est. (equiv. API)</div></Card>
          </div>

          <div className="usage-grid">
            <Card>
              <div className="card-title-row"><strong>Por modelo</strong></div>
              <table className="usage-table">
                <thead><tr><th>modelo</th><th>entrada</th><th>saída</th><th>runs</th><th>custo est.</th></tr></thead>
                <tbody>
                  {data.byModel.map((m) => (
                    <tr key={m.model}><td>{m.model}</td><td>{fmtNum(m.inputTokens)}</td><td>{fmtNum(m.outputTokens)}</td><td>{m.runs}</td><td>{fmtUsd(m.costUsd)}</td></tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card>
              <div className="card-title-row"><strong>Por tipo</strong></div>
              <table className="usage-table">
                <thead><tr><th>tipo</th><th>runs</th><th>custo est.</th></tr></thead>
                <tbody>
                  {data.byKind.map((k) => (
                    <tr key={k.kind}><td>{k.kind}</td><td>{k.runs}</td><td>{fmtUsd(k.costUsd)}</td></tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <Card style={{ marginTop: 16 }}>
            <div className="card-title-row"><strong>Custo estimado por dia</strong></div>
            <div className="usage-bars">
              {data.byDay.map((d) => (
                <div key={d.day} className="usage-bar-row" title={`${d.day}: ${fmtUsd(d.costUsd)}`}>
                  <span className="usage-bar-day">{d.day.slice(5)}</span>
                  <span className="usage-bar-track"><span className="usage-bar-fill" style={{ width: `${Math.round((d.costUsd / maxDay) * 100)}%` }} /></span>
                  <span className="usage-bar-val">{fmtUsd(d.costUsd)}</span>
                </div>
              ))}
            </div>
          </Card>

          <div className="usage-note">
            <strong>Sobre o custo:</strong> com <strong>token de assinatura do Claude Code</strong> você paga o plano fixo, não por request —
            o valor em US$ é uma <strong>estimativa equivalente-API</strong> (referência de intensidade), não fatura. Os <strong>tokens são reais</strong>:
            use-os pra acompanhar volume e os limites do plano. Com uma <strong>API key</strong> (pay-per-token), o custo passa a ser real.
            <br />Observação: o custo do <strong>pipeline (orchestrator)</strong> ainda não é capturado — só as execuções da API.
          </div>
        </>
      )}
    </Page>
  );
}
